import React, { Component } from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  Button,
  TablePagination,
  TextField,
  MenuItem,
  Box,
  CircularProgress
} from "@material-ui/core";

import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

import { connect } from "react-redux";
import { reduxLoad } from "js/actions/index";
import {
  PER_PAGE_OPTIONS,
  DEFAULT_PER_PAGE_RECORDS
} from "__helpers/constants";

function mapDispatchToProps(dispatch) {
  return {
    reduxLoad: reduxLoadVal => dispatch(reduxLoad(reduxLoadVal))
  };
}
const mapStateToProps = state => {
  return {
    userInfoRedux: state.userInfo,
    timestampRedux: state.timestamp,
    reduxLoadFlag: state.reduxLoadFlag
  };
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "INVITED", label: "Invited" },
  { value: "ACTIVE", label: "Active" },
  { value: "DEACTIVATED", label: "Deactivated" }
];

const STATUS_COLORS = {
  ACTIVE: "#4caf50",
  INVITED: "#ff9800",
  DEACTIVATED: "#9e9e9e"
};

const headCells = [
  { id: "name", sorting: true, label: "Name", width: 180 },
  { id: "email", sorting: true, label: "Email", width: 220 },
  { id: "phone", sorting: true, label: "Phone", width: 140 },
  { id: "status", sorting: true, label: "Status", width: 120 },
  { id: "devicePlatform", sorting: true, label: "Device Platform", width: 140 },
  {
    id: "disclaimerAcknowledgedAt",
    sorting: true,
    label: "Disclaimer Acknowledge Date",
    width: 200
  }
];

class MaxAllyUserReportClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      search: "",
      status: "",
      groupId: "",
      facilityId: "",
      groups: [],
      facilities: [],
      rows: [],
      total: 0,
      orderBy: "name",
      direction: true, // true = asc
      perPage: DEFAULT_PER_PAGE_RECORDS,
      currentPage: 0
    };
  }

  componentDidMount() {
    this.fetchGroups();
    this.fetchFacilities();
    this.fetchReport();
  }

  fetchGroups = async () => {
    try {
      const r = await fetch(apiPath.groups, {
        method: "GET",
        headers: generalAuthTokenHeader()
      });
      const data = r.ok ? await r.json() : [];
      const groups = (Array.isArray(data) ? data : [])
        .filter(g => g.active !== false)
        .sort((a, b) => (a.groupName || "").localeCompare(b.groupName || ""));
      this.setState({ groups });
    } catch (e) {
      /* dropdown stays empty; report still works unscoped */
    }
  };

  fetchFacilities = async () => {
    try {
      const r = await fetch(`${apiPath.facilitiesList}?page=0&size=1000`, {
        method: "GET",
        headers: generalAuthTokenHeader()
      });
      const data = r.ok ? await r.json() : [];
      const facilities = (Array.isArray(data) ? data : []).sort((a, b) =>
        (a.facilityName || "").localeCompare(b.facilityName || "")
      );
      this.setState({ facilities });
    } catch (e) {
      /* ignore */
    }
  };

  buildQueryParams = () => {
    const {
      search,
      status,
      groupId,
      facilityId,
      currentPage,
      perPage,
      orderBy,
      direction
    } = this.state;
    const queryParams = [];
    if (search && search.trim()) {
      queryParams.push(`search=${encodeURIComponent(search.trim())}`);
    }
    if (status) {
      queryParams.push(`status=${status}`);
    }
    if (groupId) {
      queryParams.push(`groupId=${groupId}`);
    }
    if (facilityId) {
      queryParams.push(`facilityId=${facilityId}`);
    }
    queryParams.push(`page=${currentPage}`);
    queryParams.push(`size=${perPage}`);
    queryParams.push(`sort=${orderBy}`);
    queryParams.push(`direction=${direction ? "asc" : "desc"}`);
    return queryParams.join("&");
  };

  fetchReport = async () => {
    this.setState({ loading: true });
    try {
      const url = `${apiPath.maxAllyUserReport}?${this.buildQueryParams()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: generalAuthTokenHeader()
      });
      if (!response.ok) {
        throw new Error("Failed to load report");
      }
      const data = await response.json();
      this.setState({
        rows: data.rows || [],
        total: data.total || 0,
        loading: false
      });
    } catch (error) {
      console.error("Error loading MaxAlly user report:", error);
      this.setState({ loading: false, rows: [], total: 0 });
      userService.showNotification({
        title: "MaxAlly User Report",
        message: "Failed to load report. Please try again.",
        type: "danger"
      });
    }
  };

  handleSearchChange = event => {
    this.setState({ search: event.target.value });
  };

  handleSearchSubmit = () => {
    this.setState({ currentPage: 0 }, this.fetchReport);
  };

  handleFilterChange = field => event => {
    this.setState(
      { [field]: event.target.value, currentPage: 0 },
      this.fetchReport
    );
  };

  tableSortHandler = property => () => {
    this.setState(
      prevState => ({
        orderBy: property,
        direction: prevState.orderBy === property ? !prevState.direction : true,
        currentPage: 0
      }),
      this.fetchReport
    );
  };

  handleChangePage = (event, newPage) => {
    this.setState({ currentPage: newPage }, this.fetchReport);
  };

  handleChangeRowsPerPage = event => {
    this.setState(
      { perPage: parseInt(event.target.value), currentPage: 0 },
      this.fetchReport
    );
  };

  handleDownloadExcel = async () => {
    userService.showNotification({
      title: "MaxAlly User Report",
      message: "Downloading Excel, please wait...",
      type: "info"
    });
    try {
      const {
        search,
        status,
        groupId,
        facilityId,
        orderBy,
        direction
      } = this.state;
      const queryParams = [];
      if (search && search.trim()) {
        queryParams.push(`search=${encodeURIComponent(search.trim())}`);
      }
      if (status) {
        queryParams.push(`status=${status}`);
      }
      if (groupId) {
        queryParams.push(`groupId=${groupId}`);
      }
      if (facilityId) {
        queryParams.push(`facilityId=${facilityId}`);
      }
      queryParams.push(`sort=${orderBy}`);
      queryParams.push(`direction=${direction ? "asc" : "desc"}`);
      const url = `${apiPath.maxAllyUserReportDownload}?${queryParams.join(
        "&"
      )}`;
      await userService.fetchsReferralReportData(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      userService.showNotification({
        title: "MaxAlly User Report",
        message: "Failed to download Excel report.",
        type: "danger"
      });
    }
  };

  formatDate = value => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  render() {
    const {
      loading,
      search,
      status,
      groupId,
      facilityId,
      groups,
      facilities,
      rows,
      total,
      orderBy,
      direction,
      perPage,
      currentPage
    } = this.state;

    return (
      <div>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="success" className="cusCardTitle">
                <h4 className="cusCardTitleText">MaxAlly User Report</h4>
              </CardHeader>
              <CardBody>
                {/* Filters Row */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ gap: 16, marginBottom: 16 }}
                >
                  <Box style={{ minWidth: 220 }}>
                    <TextField
                      label="Search (name / email / phone)"
                      value={search}
                      onChange={this.handleSearchChange}
                      onKeyPress={e => {
                        if (e.key === "Enter") {
                          this.handleSearchSubmit();
                        }
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box style={{ minWidth: 160 }}>
                    <TextField
                      select
                      label="Status"
                      value={status}
                      onChange={this.handleFilterChange("status")}
                      fullWidth
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box style={{ minWidth: 200 }}>
                    <TextField
                      select
                      label="Region Group"
                      value={groupId}
                      onChange={this.handleFilterChange("groupId")}
                      fullWidth
                    >
                      <MenuItem value="">All Region Groups</MenuItem>
                      {groups.map(g => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.groupName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box style={{ minWidth: 200 }}>
                    <TextField
                      select
                      label="Facility"
                      value={facilityId}
                      onChange={this.handleFilterChange("facilityId")}
                      fullWidth
                    >
                      <MenuItem value="">All Facilities</MenuItem>
                      {facilities.map(f => (
                        <MenuItem key={f.facilityId} value={f.facilityId}>
                          {f.facilityName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.handleSearchSubmit}
                      disabled={loading}
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      onClick={this.handleDownloadExcel}
                      disabled={loading}
                      style={{
                        marginLeft: 10,
                        backgroundColor: "#4caf50",
                        color: "#fff"
                      }}
                    >
                      Download Excel
                    </Button>
                  </Box>
                </Box>

                {/* Data Table */}
                {loading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <div style={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {headCells.map(headCell => (
                              <TableCell
                                key={headCell.id}
                                style={{
                                  fontWeight: "bold",
                                  fontSize: 13,
                                  minWidth: headCell.width,
                                  padding: "8px 12px"
                                }}
                              >
                                {headCell.sorting ? (
                                  <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={direction ? "asc" : "desc"}
                                    onClick={this.tableSortHandler(headCell.id)}
                                  >
                                    {headCell.label}
                                  </TableSortLabel>
                                ) : (
                                  headCell.label
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.length ? (
                            rows.map(row => (
                              <TableRow key={row.id}>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  {row.name || "—"}
                                </TableCell>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  {row.email || "—"}
                                </TableCell>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  {row.phone || "—"}
                                </TableCell>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  <span
                                    style={{
                                      color:
                                        STATUS_COLORS[row.status] || "#333",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    {row.status || "—"}
                                  </span>
                                </TableCell>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  {row.devicePlatform || "—"}
                                </TableCell>
                                <TableCell
                                  style={{ padding: "8px 12px", fontSize: 13 }}
                                >
                                  {this.formatDate(
                                    row.disclaimerAcknowledgedAt
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={headCells.length}
                                align="center"
                              >
                                <div className="not-found-text">
                                  No MaxAlly users found for the selected
                                  criteria.
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {total > 0 && (
                      <TablePagination
                        rowsPerPageOptions={PER_PAGE_OPTIONS}
                        component="div"
                        count={total}
                        rowsPerPage={perPage}
                        page={currentPage}
                        onChangePage={this.handleChangePage}
                        onChangeRowsPerPage={this.handleChangeRowsPerPage}
                      />
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

export const MaxAllyUserReport = connect(
  mapStateToProps,
  mapDispatchToProps
)(MaxAllyUserReportClass);

export default MaxAllyUserReport;
